import { gql } from '@apollo/client';
import { INSTANCE_FIELDS_FRAGMENT } from './fragments';

/**
 * List all instances for tenant (paginated)
 */
export const LIST_INSTANCES = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  query ListInstances($tenantId: String!, $limit: Int, $cursor: String) {
    listInstances(tenantId: $tenantId, limit: $limit, cursor: $cursor) {
      items {
        ...InstanceFields
      }
      total
      nextCursor
      hasMore
    }
  }
`;

/**
 * Get single instance by ID
 */
export const GET_INSTANCE = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  query GetInstance($tenantId: String!, $instanceId: String!) {
    getInstance(tenantId: $tenantId, instanceId: $instanceId) {
      ...InstanceFields
    }
  }
`;

/**
 * List instances by status with pagination
 */
export const LIST_INSTANCES_BY_STATUS = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  query ListInstancesByStatus($tenantId: String!, $status: InstanceStatus!, $limit: Int) {
    listInstancesByStatus(tenantId: $tenantId, status: $status, limit: $limit) {
      items {
        ...InstanceFields
      }
      total
      nextCursor
      hasMore
    }
  }
`;

/**
 * List instances by workflow definition
 */
export const LIST_INSTANCES_BY_WORKFLOW = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  query ListInstancesByWorkflow($tenantId: String!, $workflowDefinitionId: String!, $limit: Int) {
    listInstancesByWorkflow(
      tenantId: $tenantId
      workflowDefinitionId: $workflowDefinitionId
      limit: $limit
    ) {
      items {
        ...InstanceFields
      }
      total
      nextCursor
      hasMore
    }
  }
`;

/**
 * Get instance by correlation ID
 */
export const GET_INSTANCE_BY_CORRELATION = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  query GetInstanceByCorrelation($tenantId: String!, $correlationId: String!) {
    getInstanceByCorrelation(tenantId: $tenantId, correlationId: $correlationId) {
      ...InstanceFields
    }
  }
`;

/**
 * List active instances (Running, Paused, Compensating)
 */
export const LIST_ACTIVE_INSTANCES = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  query ListActiveInstances($tenantId: String!, $limit: Int) {
    listActiveInstances(tenantId: $tenantId, limit: $limit) {
      items {
        ...InstanceFields
      }
      total
      nextCursor
      hasMore
    }
  }
`;

/**
 * Get complete instance history (Pure Append-Only Architecture)
 *
 * Returns ALL versions of an instance sorted chronologically (V1 → Vn).
 * Uses DynamoDB GSI5 for <10ms query performance.
 *
 * @example
 * ```typescript
 * const { data } = await client.query<Record<string, any>>({
 *   query: GET_INSTANCE_HISTORY,
 *   variables: {
 *     tenantId: "healthcare-tenant",
 *     workflowDefinitionId: "wf_def_123",
 *     instanceId: "inst_abc"
 *   }
 * });
 * // data?.getInstanceHistory = [
 * //   { version: 1, status: "Pending", parentId: null },
 * //   { version: 2, status: "Running", parentId: "inst_abc" },
 * //   { version: 3, status: "Completed", parentId: "inst_def" }
 * // ]
 * // All share same rootInstanceId = "inst_abc"
 * ```
 */
export const GET_INSTANCE_HISTORY = gql`
  ${INSTANCE_FIELDS_FRAGMENT}
  query GetInstanceHistory(
    $tenantId: String!
    $workflowDefinitionId: String!
    $instanceId: String!
  ) {
    getInstanceHistory(
      tenantId: $tenantId
      workflowDefinitionId: $workflowDefinitionId
      instanceId: $instanceId
    ) {
      ...InstanceFields
    }
  }
`;
